import { META_UPGRADES, upgradeCost } from '../data/metaUpgrades.js';
import { audio } from '../systems/audio/AudioSystem.js';

/** Shop page: spends Echoes on permanent meta upgrades, via SaveManager. */
export class ShopEchoMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay shop-detail-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _render() {
    // A purchase re-renders the whole page from scratch (like every other
    // screen in this app), which would otherwise reset the scrollable
    // .shop-rows column back to the top on every buy — annoying when
    // upgrading something near the bottom of a scrolled list. Capture the
    // scroll position before the wipe and restore it after.
    const rowsEl = this.el.querySelector('.shop-rows');
    const scrollPosition = rowsEl?.scrollTop ?? 0;
    this.el.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'menu-title';
    title.textContent = 'ECHO UPGRADES';
    this.el.appendChild(title);
    const label = document.createElement('div');
    label.className = 'menu-kicker';
    label.textContent = 'PERMANENT PROGRESSION';
    this.el.insertBefore(label, title);

    const echoes = document.createElement('div');
    echoes.className = 'menu-echoes';
    echoes.innerHTML = `Echoes: <span class="menu-echoes-value">${this.save.echoes}</span>`;
    this.el.appendChild(echoes);

    const rows = document.createElement('div');
    rows.className = 'shop-rows';
    for (const def of Object.values(META_UPGRADES)) {
      rows.appendChild(this._buildUpgradeRow(def));
    }
    this.el.appendChild(rows);
    const hint = document.createElement('div');
    hint.className = 'scroll-hint';
    hint.textContent = '▼ Scroll to browse';
    this.el.appendChild(hint);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);

    rows.scrollTop = scrollPosition;
  }

  _buildUpgradeRow(def) {
    const level = this.save.getUpgradeLevel(def.id);
    const maxed = !def.repeatable && level >= def.maxLevel;
    const cost = maxed ? null : upgradeCost(def, level);
    const canAfford = !maxed && this.save.echoes >= cost;
    const bulkCost = def.repeatable
      ? Array.from({ length: 10 }, (_, i) => upgradeCost(def, level + i)).reduce((sum, value) => sum + value, 0)
      : null;
    const canAffordBulk = def.repeatable && this.save.echoes >= bulkCost;

    const row = document.createElement('div');
    row.className = 'shop-row';

    // Filled-pip track for finite upgrades — level reads at a glance without
    // parsing "Lv 3/10". Repeatable (∞) upgrades keep the text-only readout.
    const pips = def.repeatable
      ? ''
      : `<span class="shop-row-pips" aria-hidden="true">${Array.from({ length: def.maxLevel }, (_, i) => `<i class="shop-pip${i < level ? ' shop-pip-filled' : ''}"></i>`).join('')}</span>`;
    const info = document.createElement('div');
    info.className = 'shop-row-info';
    info.innerHTML = `
        <div><span class="shop-row-name">${def.name}</span> <span class="shop-row-level">Lv ${level}${def.repeatable ? ' ∞' : `/${def.maxLevel}`}</span>${pips}</div>
      <div class="shop-row-desc">${def.description}</div>
    `;
    row.appendChild(info);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'menu-button menu-button-small';
    button.textContent = maxed ? 'MAX' : `Buy — ${cost}`;
    button.disabled = maxed || !canAfford;
    button.addEventListener('click', () => {
      if (this.save.spendEchoes(cost)) {
        this.save.setUpgradeLevel(def.id, level + 1);
        audio.playSfx('levelup');
        this._render();
      }
    });
    row.appendChild(button);

    if (def.repeatable) {
      const bulkButton = document.createElement('button');
      bulkButton.type = 'button';
      bulkButton.className = 'menu-button menu-button-small';
      bulkButton.textContent = `Buy 10× — ${bulkCost}`;
      bulkButton.disabled = !canAffordBulk;
      bulkButton.addEventListener('click', () => {
        if (this.save.spendEchoes(bulkCost)) {
          this.save.setUpgradeLevel(def.id, level + 10);
          audio.playSfx('levelup');
          this._render();
        }
      });
      row.appendChild(bulkButton);
    }

    return row;
  }

  destroy() {
    this.el.remove();
  }
}
